import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { handlePagingAll } from "../../components/api/APIUtilities";
import ListResponsive from "../../components/other/ListResponsive";
import {
  BCPArrowLeft,
  BCPArrowUpArrowDown,
  BCPFilter,
  BCPSquarePlus,
  BCPUsers,
  BCPXmark,
} from "../../components/icons/bcp/BCPIcons";
import OrganizationCard from "../../components/layout/OrganizationCard";
import InfoContainer from "../../components/layout/InfoContainer";
import { isAppMode } from "../../global/AppOverlay";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import OptionsMenu from "../../components/layout/OptionsMenu";
import {
  clearFilterSx,
  filterAndSortGameStores,
  getFilterSx,
  updateFilters,
} from "../../components/utilities/SortFilterFunctions";

export default function OrganizationsOrganize() {
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [gameStores, setGameStores] = useState(null);
  const [filteredGameStores, setFilteredGameStores] = useState(null);

  // Filters and sorting
  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);

  const appMode = isAppMode();
  const theme = useTheme();
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.dashboards.organize"
  });

  useEffect(() => {
    const check = async () => {
      var apiGameStores = await handlePagingAll(`/gamestores`, {
        myStores: true,
        sortAscending: false,
        "expand[]": ["location", "owner", "gameStore", "user"],
      });

      apiGameStores.forEach((apiGameStore) => {
        if (apiGameStore.location) {
          apiGameStore.location.displayLocation = "";
          if (apiGameStore.location.city && apiGameStore.location.city !== "") {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation += apiGameStore.location.city;
          }
          if (
            apiGameStore.location.state &&
            apiGameStore.location.state !== ""
          ) {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation +=
              apiGameStore.location.state;
          }
          if (
            apiGameStore.location.country &&
            apiGameStore.location.country !== ""
          ) {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation +=
              apiGameStore.location.country;
          }
        }
      });
      setGameStores(apiGameStores);
      setLoading(false);
    };
    check();
  }, []);

  useEffect(() => {
    if (gameStores?.length && filters.length) {
      const tempGameStores = filterAndSortGameStores(gameStores, filters);

      setFilteredGameStores(tempGameStores);
    } else if (!filters.length) {
      setFilteredGameStores(null);
    }
    // eslint-disable-next-line
  }, [filters, gameStores]);

  return (
    <Box width="100%">
      <Container
        maxWidth="lg"
        sx={{
          pt: 2,
          pb: 2,
        }}
      >
        <Grid
          container
          sx={{
            p: 2,
            gap: 2,
          }}
        >
          <Grid item xs={12}>
            <Link
              href="/organize/dashboard"
              color="link.main"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <BCPArrowLeft
                sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
              />{" "}
              Back to Host Dashboard
            </Link>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "block", md: "flex" },
              justifyContent: "space-between",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={9}>
                <Typography
                  variant="h2"
                  component="h3"
                  fontWeight="700"
                  gutterBottom
                >
                  {t("myOrganizations")}
                </Typography>
              </Grid>

              <Hidden mdDown>
                {!(!loading && !gameStores?.length) && (
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      href="/organize/organization"
                      target={`${appMode ? "_blank" : "_self"}`}
                      startIcon={<BCPUsers />}
                      fullWidth
                    >
                      {t("createOrganization")}
                    </Button>
                  </Grid>
                )}
              </Hidden>
            </Grid>
          </Grid>

          {!loading && !gameStores?.length ? (
            <Grid item xs={12}>
              <InfoContainer
                title={t("areYouGameStoreTitle")}
                body={t("areYouGameStoreDescription")}
                ActionButton={
                  <Button
                    variant="contained"
                    href="/organize/organization"
                    target={`${appMode ? "_blank" : "_self"}`}
                    startIcon={<BCPSquarePlus />}
                  >
                    {t("createOrganization")}
                  </Button>
                }
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <ListResponsive
                originalList={
                  loading
                    ? null
                    : filteredGameStores
                      ? filteredGameStores
                      : gameStores
                }
                doSearch
                doPages
                initialRowsPerPage={24}
                actionButton={{
                  props: {
                    variant: "contained",
                    color: "primary",
                    fullWidth: true,
                    href: "/organize/organization",
                    target: `${appMode ? "_blank" : "_self"}`,
                    startIcon: <BCPUsers />,
                    sx: {
                      display: { xs: "flex", md: "none" }
                    }
                  },
                  label: t("createOrganization")
                }}
                actionButtonBottom={[
                  {
                    label: t("sort"),
                    startIcon: <BCPArrowUpArrowDown />,
                    onClick: (e) => {
                      setAnchorEl(e.currentTarget);
                    },
                    disabled: loading || !gameStores?.length,
                    sx: {
                      ...getFilterSx(
                        Boolean(
                          filters.length &&
                            filters.some((item) => item.includes("sort"))
                        ),
                        filters,
                        theme
                      ),
                    },
                  },
                  {
                    label: t("filters"),
                    startIcon: <BCPFilter />,
                    onClick: (e) => {
                      setAnchorEl2(e.currentTarget);
                    },
                    disabled: loading || !gameStores?.length,
                    sx: {
                      ...getFilterSx(
                        Boolean(
                          filters.length &&
                            filters.some((item) => item.includes("filter"))
                        ),
                        filters,
                        theme
                      ),
                    },
                  },
                  {
                    label: t("clearFilters"),
                    startIcon: <BCPXmark />,
                    disabled: loading || !gameStores?.length,
                    sx: {
                      ...clearFilterSx(false, filters, theme),
                    },
                    onClick: () => {
                      updateFilters(null, null, null, setFilters);
                      setFilteredGameStores(null);
                      setAnchorEl(null);
                      setAnchorEl2(null);
                    },
                  },
                ]}
                doSort
                searchProps={{
                  label: t("searchOrganizations"),
                  performSearch: (originalList, searchText) => {
                    if (!searchText || searchText === "") {
                      return originalList;
                    } else {
                      var filteredList = originalList.filter(function (el) {
                        if (el?.name?.toLowerCase().includes(searchText))
                          return true;
                        if (
                          el?.owner?.firstName
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          return true;
                        if (
                          el?.owner?.lastName
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          return true;
                        if (
                          el?.location?.displayLocation
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          return true;

                        return false;
                      });
                      return filteredList;
                    }
                  },
                }}
                rowProps={{
                  mapFunction: (row, index) => (
                    <Grid
                      key={row?.id || index}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      sx={{ p: 1 }}
                    >
                      <OrganizationCard
                        organize
                        organization={row}
                        authContext={authContext}
                        key={row?.id || index}
                        preventImageFetch
                        activeTab={"overview"}
                      />
                    </Grid>
                  ),
                }}
              />
            </Grid>
          )}
        </Grid>

        {/* Sorting */}
        <OptionsMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          items={[
            {
              name: "sort-a-z",
              label: t("nameAToZ"),
              selected: filters.includes("sort-a-z"),
              onClick: () =>
                updateFilters("sort-a-z", false, filters, setFilters),
            },
            {
              name: "sort-z-a",
              label: t("nameZToA"),
              selected: filters.includes("sort-z-a"),
              onClick: () =>
                updateFilters("sort-z-a", false, filters, setFilters),
            },
            {
              name: "sort-newest",
              label: t("dateCreatedNewest"),
              selected: filters.includes("sort-newest"),
              onClick: () =>
                updateFilters("sort-newest", false, filters, setFilters),
            },
            {
              name: "sort-oldest",
              label: t("dateCreatedOldest"),
              selected: filters.includes("sort-oldest"),
              onClick: () =>
                updateFilters("sort-oldest", false, filters, setFilters),
            },
          ]}
          name="sort-menu"
        />

        {/* Filters */}
        <OptionsMenu
          anchorEl={anchorEl2}
          setAnchorEl={setAnchorEl2}
          items={[
            {
              name: "filter-active",
              label: t("active"),
              selected: filters.includes("filter-active"),
              onClick: () =>
                updateFilters(
                  "filter-active",
                  "filter-inactive",
                  filters,
                  setFilters
                ),
            },
            {
              name: "filter-inactive",
              label: t("inactive"),
              selected: filters.includes("filter-inactive"),
              onClick: () =>
                updateFilters(
                  "filter-inactive",
                  "filter-active",
                  filters,
                  setFilters
                ),
            },
            { divider: true },
            {
              name: "filter-approved",
              label: t("approved"),
              selected: filters.includes("filter-approved"),
              onClick: () =>
                updateFilters(
                  "filter-approved",
                  "filter-not-approved",
                  filters,
                  setFilters
                ),
            },
            {
              name: "filter-not-approved",
              label: t("notApproved"),
              selected: filters.includes("filter-not-approved"),
              onClick: () =>
                updateFilters(
                  "filter-not-approved",
                  "filter-approved",
                  filters,
                  setFilters
                ),
            },
          ]}
          name="filter-menu"
        />
      </Container>
    </Box>
  );
}
